
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { GoldPurity, SilverPurity } from '@/lib/pricing';

export interface DealerPurities {
  ORO: GoldPurity[];
  ARGENTO: SilverPurity[];
}

export function useDealerPurities(dealerId: string) {
  const [purities, setPurities] = useState<DealerPurities>({ ORO: [], ARGENTO: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!dealerId) {
      setLoading(false);
      return;
    }

    const fetchPurities = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error } = await supabase
          .from('vw_active_quotations')
          .select('metal, purity')
          .eq('dealer_id', dealerId);

        if (error) {
          throw error;
        }
        
        const groupedPurities: DealerPurities = { ORO: [], ARGENTO: [] };
        if (data) {
          data.forEach(item => {
            if (item.metal === 'ORO') {
              groupedPurities.ORO.push(item.purity as GoldPurity);
            } else if (item.metal === 'ARGENTO') {
              groupedPurities.ARGENTO.push(item.purity as SilverPurity);
            }
          });
        }
        
        // Ordinamento delle purezze secondo l'ordine richiesto: 24KT → 22KT → 20KT → 18KT → 14KT → 9KT
        const goldPurityOrder = ['24KT', '22KT', '20KT', '18KT', '14KT', '9KT'];
        // Ordine per l'argento: PURO (999) → 925 → 800 (dal più puro al meno puro)
        const silverPurityOrder = ['PURO', '999', '925', '800'];
        
        groupedPurities.ORO.sort((a, b) => {
          const indexA = goldPurityOrder.indexOf(a);
          const indexB = goldPurityOrder.indexOf(b);
          // Se una purezza non è nell'ordine predefinito, la mette alla fine
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });
        
        groupedPurities.ARGENTO.sort((a, b) => {
          const indexA = silverPurityOrder.indexOf(a);
          const indexB = silverPurityOrder.indexOf(b);
          // Se una purezza non è nell'ordine predefinito, la mette alla fine
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });

        setPurities(groupedPurities);
      } catch (e: any) {
        setError(e.message);
        console.error("Error fetching dealer purities:", e);
      } finally {
        setLoading(false);
      }
    };

    fetchPurities();
  }, [dealerId]);

  return { purities, loading, error };
}
